import { useState } from 'react';
import { useNewAccounts, useCreateNewAccount, useUpdateNewAccount, useSubmitNewAccount } from '../hooks/useNewAccounts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createNewAccountRequestSchema, type NewAccountRequest, type CreateNewAccountRequestInput, type HubSpotContact } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { HubSpotContactSearch } from '../components/HubSpotContactSearch';
import { PlusIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function NewAccountPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const { data, isLoading } = useNewAccounts(statusFilter || undefined, page);
  const createMutation = useCreateNewAccount();
  const updateMutation = useUpdateNewAccount();
  const submitMutation = useSubmitNewAccount();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<NewAccountRequest | null>(null);
  const [selectedHubSpotId, setSelectedHubSpotId] = useState<string | null>(null);

  const { register, handleSubmit, reset, watch, setValue, getValues, formState: { errors } } = useForm<CreateNewAccountRequestInput>({
    resolver: zodResolver(createNewAccountRequestSchema) as never,
  });

  const isIRA = watch('is_ira');

  function openNew() {
    setEditing(null);
    setSelectedHubSpotId(null);
    reset({
      account_title: '', account_type: '', is_ira: false, ira_custodian: '',
      contact_name: '', contact_email: '', contact_phone: '',
      address1: '', address2: '', city: '', state: '', zip: '', country: 'US',
      hubspot_contact_id: undefined,
    });
    setModalOpen(true);
  }

  function openEdit(row: NewAccountRequest) {
    if (row.status !== 'draft') return;
    setEditing(row);
    setSelectedHubSpotId(row.hubspot_contact_id ?? null);
    reset({
      account_title: row.account_title,
      account_type: row.account_type ?? '',
      is_ira: row.is_ira,
      ira_custodian: row.ira_custodian ?? '',
      contact_name: row.contact_name ?? '',
      contact_email: row.contact_email ?? '',
      contact_phone: row.contact_phone ?? '',
      address1: row.address1 ?? '',
      address2: row.address2 ?? '',
      city: row.city ?? '',
      state: row.state ?? '',
      zip: row.zip ?? '',
      country: row.country ?? 'US',
      hubspot_contact_id: row.hubspot_contact_id ?? undefined,
    });
    setModalOpen(true);
  }

  function handleContactSelect(contact: HubSpotContact) {
    setSelectedHubSpotId(contact.id);
    setValue('hubspot_contact_id', contact.id);

    // Auto-fill form fields from HubSpot contact properties
    const p = contact.properties;

    const fullName = [p.firstname, p.lastname].filter(Boolean).join(' ');
    if (fullName) setValue('contact_name', fullName);
    if (p.email) setValue('contact_email', p.email);
    if (p.phone) setValue('contact_phone', p.phone);
    if (p.address) setValue('address1', p.address);
    if (p.city) setValue('city', p.city);
    if (p.state) setValue('state', p.state);
    if (p.zip) setValue('zip', p.zip);
    if (p.country) setValue('country', p.country);

    // Fill account_title with company if currently empty
    const currentTitle = getValues('account_title');
    if (!currentTitle && p.company) {
      setValue('account_title', p.company);
    }

    toast.success(`Contact linked: ${fullName || p.email || contact.id}`);
  }

  async function onSubmit(formData: CreateNewAccountRequestInput) {
    try {
      // Include hubspot_contact_id in the payload
      const payload = { ...formData, hubspot_contact_id: selectedHubSpotId ?? undefined };
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...payload });
        toast.success('Account request updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Account request draft created');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  async function handleSubmitToSFTP(id: number, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm('Generate Excel and upload to FideliTrade SFTP?')) return;
    try {
      await submitMutation.mutateAsync(id);
      toast.success('New account request submitted to SFTP');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit');
    }
  }

  const requests = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-silver-500">{pagination?.total ?? 0} requests</p>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-silver-300 px-3 py-1.5 text-sm">
            <option value="">All Statuses</option>
            {['draft', 'uploaded', 'processing', 'completed', 'exception', 'cancelled'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <Button onClick={openNew}><PlusIcon className="h-4 w-4" /> New Account Request</Button>
      </div>

      <Card padding={false}>
        <Table<NewAccountRequest>
          loading={isLoading}
          data={requests}
          onRowClick={openEdit}
          columns={[
            { key: 'id', header: 'ID' },
            { key: 'account_title', header: 'Account Title' },
            { key: 'account_type', header: 'Type' },
            { key: 'is_ira', header: 'IRA', render: (r) => r.is_ira ? 'Yes' : 'No' },
            { key: 'contact_name', header: 'Contact' },
            { key: 'result_account_number', header: 'Assigned #', render: (r) => r.result_account_number ?? '-' },
            { key: 'status', header: 'Status', render: (r) => <Badge>{r.status}</Badge> },
            {
              key: 'actions', header: '',
              render: (r) => r.status === 'draft' ? (
                <Button size="sm" onClick={(e) => handleSubmitToSFTP(r.id, e)} loading={submitMutation.isPending}>
                  <PaperAirplaneIcon className="h-4 w-4" /> Submit
                </Button>
              ) : null,
            },
          ]}
        />
        {pagination && (
          <div className="px-4">
            <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Account Request' : 'New Account Request'} size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <HubSpotContactSearch
            onSelect={handleContactSelect}
            selectedContactId={selectedHubSpotId}
            onClear={() => {
              setSelectedHubSpotId(null);
              setValue('hubspot_contact_id', undefined);
            }}
          />

          <hr className="border-silver-200" />

          <Input label="Account Title" {...register('account_title')} error={errors.account_title?.message} />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Account Type" placeholder="Select type..." options={[
              { value: 'Individual', label: 'Individual' },
              { value: 'Joint', label: 'Joint' },
              { value: 'IRA', label: 'IRA' },
              { value: 'Trust', label: 'Trust' },
              { value: 'Corporate', label: 'Corporate' },
            ]} {...register('account_type')} />
            <div className="flex items-end gap-2 pb-1">
              <input type="checkbox" id="is_ira" {...register('is_ira')} className="h-4 w-4 rounded border-silver-300 text-gold-600" />
              <label htmlFor="is_ira" className="text-sm text-silver-700">IRA Account</label>
            </div>
          </div>
          {isIRA && <Input label="IRA Custodian" {...register('ira_custodian')} />}
          <div className="grid grid-cols-3 gap-4">
            <Input label="Contact Name" {...register('contact_name')} />
            <Input label="Contact Email" type="email" {...register('contact_email')} />
            <Input label="Contact Phone" {...register('contact_phone')} />
          </div>
          <Input label="Address 1" {...register('address1')} />
          <Input label="Address 2" {...register('address2')} />
          <div className="grid grid-cols-4 gap-4">
            <Input label="City" {...register('city')} />
            <Input label="State" {...register('state')} />
            <Input label="ZIP" {...register('zip')} />
            <Input label="Country" {...register('country')} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Update Draft' : 'Save Draft'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
